import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QHubBuddyLogo, QHubFooter } from "@/components/QHubBranding";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";

const GENERIC_MESSAGE =
  "Si existe una cuenta asociada a ese correo, recibirás instrucciones para restablecer tu contraseña.";

const ForgotPasswordB2C = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/password-reset/request-b2c`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim().toLowerCase() }),
        },
      );
      // Any status other than 429 is treated as accepted — the backend
      // always returns 202 on the happy path with a generic message.
      if (res.status === 429) {
        setError(
          "Demasiados intentos. Por favor espera un minuto y vuelve a intentarlo.",
        );
      } else {
        setSent(true);
      }
    } catch (_err) {
      // Network error — show the same generic screen to preserve the
      // no-enumeration guarantee (a real backend outage would still not
      // leak account existence).
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-mesh" data-testid="forgot-password-b2c-page">
      <header className="qhub-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <QHubBuddyLogo size="default" />
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              data-testid="forgot-back-to-login"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver al login
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md bg-white rounded-3xl p-8 border border-gray-100 shadow-xl shadow-gray-200/50">
          {sent ? (
            <div data-testid="forgot-generic-success">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h1 className="text-xl font-bold font-['Outfit'] text-gray-900 text-center mb-3">
                Revisa tu correo
              </h1>
              <p className="text-sm text-gray-600 text-center mb-6">
                {GENERIC_MESSAGE}
              </p>
              <p className="text-xs text-gray-500 text-center">
                El enlace expira en 30 minutos y solo puede usarse una vez.
              </p>
              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="text-sm font-semibold text-[#419cca] hover:underline"
                  data-testid="forgot-return-login-cta"
                >
                  Volver al login
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#419cca] to-[#8b5cf6] flex items-center justify-center shadow-lg shadow-[#419cca]/20">
                <Mail className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-xl font-bold font-['Outfit'] text-gray-900 text-center mb-2">
                ¿Olvidaste tu contraseña?
              </h1>
              <p className="text-sm text-gray-500 text-center mb-6">
                Escribe el correo con el que ingresas a BUDDY AVANZA y te enviaremos un enlace
                para elegir una nueva contraseña.
              </p>

              <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
                <Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Correo electrónico"
                  type="email"
                  autoComplete="email"
                  className="h-12 rounded-xl"
                  data-testid="forgot-email-input"
                  required
                />

                {error && (
                  <p
                    className="text-sm text-red-600 bg-red-50 rounded-lg p-2.5"
                    data-testid="forgot-error"
                  >
                    {error}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-[#419cca] to-[#8b5cf6] hover:opacity-90 text-white font-medium text-base"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? "Enviando…" : "Enviar enlace de recuperación"}
                </Button>
              </form>

              <p className="mt-5 text-xs text-gray-400 text-center">
                Por seguridad, siempre mostraremos la misma confirmación
                independientemente de si existe una cuenta con ese correo.
              </p>
            </>
          )}
        </div>
      </main>

      <QHubFooter />
    </div>
  );
};

export default ForgotPasswordB2C;
